const readCourseNumber = (course) => {
  const number = Number(course?.course_number ?? course?.courseNumber);
  return Number.isInteger(number) && number > 0 ? number : null;
};

const readCourseStatus = (course) =>
  String(course?.status || "held").trim().toLowerCase();

export const resolveLatestCourseNumber = (courses = [], fallback = 1) => {
  const availableNumbers = (Array.isArray(courses) ? courses : [])
    .filter((course) => readCourseStatus(course) !== "cancelled")
    .map(readCourseNumber)
    .filter(Boolean);
  return availableNumbers.length > 0
    ? Math.max(...availableNumbers)
    : Math.max(1, Number(fallback) || 1);
};

export const resolveNextCourseNumber = (courses = [], fallback = 1) =>
  resolveLatestCourseNumber(courses, fallback) + 1;

export const hasLaterActiveCourse = (courses = [], courseNumber = 1) => {
  const target = Math.max(1, Number(courseNumber) || 1);
  return (Array.isArray(courses) ? courses : []).some((course) => {
    const number = readCourseNumber(course);
    return (
      number &&
      number > target &&
      readCourseStatus(course) !== "cancelled"
    );
  });
};

export const resolveFollowingCourseTarget = (
  courses = [],
  completedCourseNumber = 1,
) => {
  const source = Array.isArray(courses) ? courses : [];
  const completed = Math.max(1, Number(completedCourseNumber) || 1);
  const latest = resolveLatestCourseNumber(source, completed);
  const laterEditableNumbers = source
    .filter((course) => readCourseStatus(course) === "held")
    .map(readCourseNumber)
    .filter((number) => number && number > completed)
    .sort((left, right) => left - right);

  if (laterEditableNumbers.length > 0) {
    return { action: "select", courseNumber: laterEditableNumbers[0] };
  }
  if (completed < latest) {
    return {
      action: "select",
      courseNumber: resolveSelectedCourseNumber(source, latest),
    };
  }
  return { action: "create", courseNumber: latest + 1 };
};

export const resolveSelectedCourseNumber = (courses = [], fallback = 1) => {
  const editableNumbers = (Array.isArray(courses) ? courses : [])
    .filter((course) => readCourseStatus(course) === "held")
    .map(readCourseNumber)
    .filter(Boolean);
  return editableNumbers.length > 0
    ? Math.max(...editableNumbers)
    : resolveLatestCourseNumber(courses, fallback);
};

export const resolveDisplayedCourseNumber = (courses = [], requested = 1) => {
  const normalizedRequested = Math.max(1, Number(requested) || 1);
  const requestedCourse = (Array.isArray(courses) ? courses : []).find(
    (course) => readCourseNumber(course) === normalizedRequested,
  );

  // A real held course is the draft currently being composed. A missing
  // course number is not a draft: it is commonly the stale initial value left
  // by an asynchronous cart refresh, so fall back to the latest real course.
  if (requestedCourse && readCourseStatus(requestedCourse) === "held") {
    return normalizedRequested;
  }

  return resolveSelectedCourseNumber(courses, normalizedRequested);
};

export const isCourseEditable = (courses = [], courseNumber = 1) => {
  const normalizedNumber = Math.max(1, Number(courseNumber) || 1);
  const course = (Array.isArray(courses) ? courses : []).find(
    (entry) => readCourseNumber(entry) === normalizedNumber,
  );
  return !course || readCourseStatus(course) === "held";
};

export const selectCourseDraftItems = (items = [], courseNumber = 1) => {
  const selectedNumber = Math.max(1, Number(courseNumber) || 1);
  return (Array.isArray(items) ? items : []).filter(
    (item) =>
      Math.max(
        1,
        Number(item?.courseNumber ?? item?.course_number ?? 1) || 1,
      ) === selectedNumber,
  );
};
